(() => {
  if (customElements.get('ring-stack-quiz')) return;

  const CATEGORIES = { 'All Day Every Day': 'all-day', 'Special Occasion': 'occasions' };
  const VIBES = {
    'Barely There': 'minimal', 'Classic + Polished': 'classic',
    'Stacked + Layered': 'stacked', 'Big + Bold': 'bold', 'Texture + Stones': 'stones'
  };
  const VIBE_PREVIEW_IDS = {
    minimal: '5772512963', classic: '14941679386993', stacked: '4679461208139',
    bold: '6654362157251', stones: '8017519313091'
  };
  const VARIANT_FIELDS = `nodes {
    id title availableForSale price { amount currencyCode }
    image { url(transform: { maxWidth: 900 }) }
    selectedOptions { name value }
  } pageInfo { hasNextPage endCursor }`;
  const PRODUCT_FIELDS = `id handle title availableForSale requiresSellingPlan
    featuredImage { url(transform: { maxWidth: 900 }) }
    options { name }
    variants(first: 100) { ${VARIANT_FIELDS} }`;

  class RingStackQuiz extends HTMLElement {
    connectedCallback() {
      if (this.initialized) {
        if (!this.catalogueReady && !this.catalogueLoading) this.loadCatalogue();
        return;
      }
      this.initialized = true;
      this.step = 1;
      this.answers = { frequency: '', vibe: '', metal: '', budget: '' };
      this.products = [];
      this.catalogueReady = false;
      this.catalogueLoading = false;
      this.catalogueError = false;
      this.resultsRequested = false;
      this.stacks = [];
      this.submitting = false;
      this.cartFeedbackActive = false;
      this.advancing = false;
      this.swapTarget = null;
      this.hero = this.querySelector('.ring-stack-quiz__hero');
      this.quiz = this.querySelector('[data-quiz]');
      this.results = this.querySelector('[data-results]');
      this.error = this.querySelector('[data-error]');
      this.modal = this.querySelector('[data-modal]');
      this.defaultAddAllText = 'ADD ALL TO BAG';
      this.cartStatus = this.querySelector('[data-cart-status]');
      this.cartNotification = document.querySelector('cart-notification');
      this.moneyFormatter = new Intl.NumberFormat('en-US', {
        style: 'currency', currency: 'USD'
      });

      this.querySelector('[data-start]').addEventListener('click', () => this.start());
      this.querySelector('[data-form]').addEventListener('submit', (event) => event.preventDefault());
      this.querySelector('[data-back]').addEventListener('click', () => this.back());
      this.querySelector('[data-restart]').addEventListener('click', () => this.restart());
      this.querySelectorAll('[data-choice]').forEach((button) => {
        button.addEventListener('click', () => this.chooseAnswer(button));
      });
      this.querySelectorAll('[data-close-modal]').forEach((button) => {
        button.addEventListener('click', () => this.closeModal());
      });
      this.handleModalKeydown = (event) => this.onModalKeydown(event);
      this.querySelector('[data-catalogue-retry]').addEventListener('click', () => this.loadCatalogue());
      this.querySelector('[data-catalogue-back]').addEventListener('click', () => {
        this.resultsRequested = false;
        this.results.hidden = true;
        this.quiz.hidden = false;
        this.showStep(4);
        this.focusHeading();
      });
      this.loadVibePreviews();
      this.loadCatalogue();
    }

    disconnectedCallback() {
      this.catalogueController?.abort();
      clearTimeout(this.cartFeedbackTimer);
      this.closeModal();
    }

    parseCatalogue(catalogue) {
      if (catalogue.version !== 4 || !Array.isArray(catalogue.products)
        || !Array.isArray(catalogue.excludedProductIds) || !Array.isArray(catalogue.pairingRules)
        || !Array.isArray(catalogue.curatedStacks) || !Array.isArray(catalogue.promotion?.eligibleProductIds)
        || catalogue.promotion.thresholdCents !== 9800) {
        throw new Error('Invalid quiz catalogue.');
      }
      const seen = new Set();
      const overrides = catalogue.membershipOverrides || {};
      const records = catalogue.products.map((record, order) => {
        if (!/^\d+$/.test(record.id) || typeof record.id !== 'string' || seen.has(record.id) || !record.name) {
          throw new Error('Invalid or duplicate quiz product ID.');
        }
        seen.add(record.id);
        const categories = record.category.split(';;').map((value) => CATEGORIES[value.trim()]);
        const subcategories = [
          ...record.subcategory.split(';;'),
          ...(overrides[record.id]?.addSubcategories || [])
        ];
        const vibes = [...new Set(subcategories.map((value) => VIBES[value.trim()]))];
        if (categories.some((value) => !value) || vibes.some((value) => !value)) throw new Error('Unknown quiz category.');
        return { ...record, order, categories, vibes, variantFilters: catalogue.variantFilters?.[record.id] || {} };
      });
      const referencedIds = [
        ...catalogue.excludedProductIds, ...Object.keys(overrides),
        ...catalogue.pairingRules.flatMap((rule) => [...(rule.triggerProductIds || []), ...(rule.requiredAnyProductIds || [])]),
        ...catalogue.curatedStacks.flatMap((stack) => stack.productIds || []),
        ...catalogue.promotion.eligibleProductIds
      ];
      if (referencedIds.some((id) => !seen.has(id))) throw new Error('Unknown referenced quiz product.');
      this.promotion = {
        thresholdCents: catalogue.promotion.thresholdCents,
        eligibleIds: new Set(catalogue.promotion.eligibleProductIds)
      };
      this.curatedStackDefinitions = catalogue.curatedStacks.map((stack) => {
        if (![2, 3, 4].includes(stack.productIds?.length)) throw new Error('Invalid curated stack.');
        return [...stack.productIds];
      });
      this.pairingRules = catalogue.pairingRules.map((rule) => {
        const vibe = VIBES[rule.vibe];
        if (!vibe || !rule.triggerProductIds?.length || !rule.requiredAnyProductIds?.length
          || new Set(rule.triggerProductIds).size !== rule.triggerProductIds.length
          || new Set(rule.requiredAnyProductIds).size !== rule.requiredAnyProductIds.length) {
          throw new Error('Invalid quiz pairing rule.');
        }
        return {
          vibe,
          triggerIds: new Set(rule.triggerProductIds),
          requiredIds: new Set(rule.requiredAnyProductIds)
        };
      });
      return records.filter((record) => !catalogue.excludedProductIds.includes(record.id));
    }

    async requestStorefront(query, variables, signal) {
      const response = await fetch('/api/2026-07/graphql.json', {
        method: 'POST', signal,
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ query, variables: { ...variables, country: this.dataset.country } })
      });
      if (!response.ok) throw new Error(`Catalogue request failed (${response.status}).`);
      const result = await response.json();
      if (result.errors?.length || !result.data) throw new Error('Shopify could not return the complete catalogue.');
      return result.data;
    }

    async completeVariants(product, signal) {
      let connection = product.variants;
      const variants = [...connection.nodes];
      const cursors = new Set();
      while (connection.pageInfo.hasNextPage) {
        const after = connection.pageInfo.endCursor;
        if (!after || cursors.has(after)) throw new Error('Incomplete variant pagination.');
        cursors.add(after);
        const data = await this.requestStorefront(`query($id: ID!, $after: String!, $country: CountryCode!)
          @inContext(country: $country) {
            node(id: $id) { ... on Product { id variants(first: 100, after: $after) { ${VARIANT_FIELDS} } } }
          }`, { id: product.id, after }, signal);
        if (data.node?.id !== product.id) throw new Error('Product changed while loading sizes.');
        connection = data.node.variants;
        variants.push(...connection.nodes);
      }
      return variants;
    }

    priceInCents(price) {
      const match = /^(\d+)(?:\.(\d{1,2}))?$/.exec(price.amount);
      if (!match || price.currencyCode !== this.dataset.currency) throw new Error('Product currency or price could not be verified.');
      const cents = Number(match[1]) * 100 + Number((match[2] || '').padEnd(2, '0'));
      if (!Number.isSafeInteger(cents)) throw new Error('Invalid product price.');
      return cents;
    }

    normalizeProduct(record, product, variants) {
      if (product.id !== `gid://shopify/Product/${record.id}` || !product.handle) throw new Error('Product ID mismatch.');
      const optionNames = product.options.map((option) => option.name);
      const seen = new Set();
      return {
        ...record, title: product.title,
        url: `${window.Shopify?.routes?.root || '/'}products/${encodeURIComponent(product.handle)}`,
        handle: product.handle, image: product.featuredImage?.url || null, optionNames,
        variants: variants.map((variant) => {
          const id = /^gid:\/\/shopify\/ProductVariant\/(\d+)$/.exec(variant.id)?.[1];
          if (!id || seen.has(id)) throw new Error('Invalid or duplicate variant ID.');
          seen.add(id);
          const options = optionNames.map((name) => variant.selectedOptions.find((option) => option.name === name)?.value);
          if (options.some((value) => typeof value !== 'string')) throw new Error('Incomplete variant options.');
          return {
            id, title: variant.title, options, price: this.priceInCents(variant.price),
            image: variant.image?.url || product.featuredImage?.url || null,
            available: product.availableForSale && variant.availableForSale && !product.requiresSellingPlan
          };
        })
      };
    }

    async loadVibePreviews() {
      try {
        const ids = Object.values(VIBE_PREVIEW_IDS).map((id) => `gid://shopify/Product/${id}`);
        const data = await this.requestStorefront(`query($ids: [ID!]!, $country: CountryCode!)
          @inContext(country: $country) { nodes(ids: $ids) { ... on Product { id featuredImage { url(transform: { maxWidth: 400 }) } } } }`, { ids });
        this.vibePreviewImages = new Map(data.nodes.filter(Boolean).map((product) => [
          product.id.split('/').pop(), product.featuredImage?.url
        ]));
        this.updateVibeImages();
      } catch (error) {
        console.warn('Ring stack quiz preview images:', error);
      }
    }

    async loadCatalogue() {
      if (this.catalogueLoading || this.catalogueReady) return;
      this.catalogueLoading = true;
      this.catalogueError = false;
      if (this.resultsRequested) this.updateCatalogueStatus();
      const controller = new AbortController();
      this.catalogueController = controller;
      const timeout = setTimeout(() => controller.abort(), 30000);
      try {
        const response = await fetch(this.dataset.catalogueUrl, { signal: controller.signal });
        if (!response.ok) throw new Error('The quiz product list could not be loaded.');
        const records = this.parseCatalogue(await response.json());
        const loaded = new Map();
        let offset = 0;
        const worker = async () => {
          while (offset < records.length) {
            // Two concurrent, bounded batches stay below tokenless API cost limits.
            const batch = records.slice(offset, offset + 6);
            offset += 6;
            const data = await this.requestStorefront(`query($ids: [ID!]!, $country: CountryCode!)
              @inContext(country: $country) { nodes(ids: $ids) { ... on Product { ${PRODUCT_FIELDS} } } }`,
              { ids: batch.map((record) => `gid://shopify/Product/${record.id}`) }, controller.signal);
            if (!Array.isArray(data.nodes) || data.nodes.length !== batch.length) throw new Error('Incomplete catalogue response.');
            for (let index = 0; index < batch.length; index += 1) {
              const product = data.nodes[index];
              // Unpublished products return null. Keep their source IDs for future visits.
              if (product === null) continue;
              const variants = await this.completeVariants(product, controller.signal);
              loaded.set(batch[index].id, this.normalizeProduct(batch[index], product, variants));
            }
          }
        };
        await Promise.all([worker(), worker()]);
        if (controller.signal.aborted) throw new Error('Catalogue load cancelled.');
        this.products = records.filter((record) => loaded.has(record.id)).map((record) => loaded.get(record.id));
        this.catalogueReady = true;
        this.updateVibeImages();
      } catch (error) {
        controller.abort();
        this.products = [];
        this.catalogueReady = false;
        this.catalogueError = true;
        if (this.isConnected) console.warn('Ring stack quiz catalogue:', error);
      } finally {
        clearTimeout(timeout);
        this.catalogueLoading = false;
        if (this.resultsRequested && this.isConnected) {
          if (this.catalogueReady) this.showResults();
          else this.updateCatalogueStatus();
        }
      }
    }

    updateCatalogueStatus() {
      this.querySelector('[data-catalogue-status]').hidden = false;
      this.querySelector('[data-results-content]').hidden = true;
      this.querySelector('[data-catalogue-message]').textContent = this.catalogueError
        ? 'We couldn’t load the ring collection. Please try again.'
        : 'Preparing your stacks…';
      this.querySelector('[data-catalogue-retry]').hidden = !this.catalogueError;
    }

    assignVibePreviews() {
      const previews = new Map();
      for (const [vibe, id] of Object.entries(VIBE_PREVIEW_IDS)) {
        const product = this.products.find((item) => item.id === id && item.image);
        const image = this.vibePreviewImages?.get(id) || product?.image;
        if (image) previews.set(vibe, { id, image });
      }
      return previews;
    }

    updateVibeImages() {
      const previews = this.assignVibePreviews();
      this.querySelectorAll('[data-choice-group="vibe"] [data-choice]').forEach((button) => {
        const media = button.querySelector('[data-vibe-media]');
        media.querySelector('img')?.remove();
        const product = previews.get(button.dataset.choice);
        media.dataset.previewProductId = product?.id || '';
        if (!product) return;
        const image = document.createElement('img');
        image.alt = '';
        image.width = 400;
        image.height = 400;
        image.loading = 'eager';
        image.addEventListener('error', () => image.remove(), { once: true });
        image.src = product.image;
        media.append(image);
      });
    }

    start() {
      this.resultsRequested = false;
      this.hero.hidden = true;
      this.results.hidden = true;
      this.quiz.hidden = false;
      this.showStep(1);
      this.focusHeading();
    }

    chooseAnswer(button) {
      if (this.advancing || Number(button.closest('[data-step]').dataset.step) !== this.step) return;
      const group = button.closest('[data-choice-group]');
      group.querySelectorAll('[data-choice]').forEach((choice) => {
        choice.setAttribute('aria-pressed', String(choice === button));
      });
      this.answers[group.dataset.choiceGroup] = button.dataset.choice;
      this.error.textContent = '';
      this.advancing = true;
      window.requestAnimationFrame(() => {
        this.advancing = false;
        if (this.isConnected) this.next();
      });
    }

    next() {
      if (this.advancing || this.quiz.hidden) return;
      const group = ['frequency', 'vibe', 'metal', 'budget'][this.step - 1];
      if (!this.answers[group]) {
        this.error.textContent = 'Choose one option to continue.';
        this.querySelector(`[data-step="${this.step}"] [data-choice]`).focus();
        return;
      }
      if (this.step === 4) this.showResults();
      else {
        this.showStep(this.step + 1);
        this.focusHeading();
      }
    }

    back() {
      if (this.advancing || this.step <= 1) return;
      this.showStep(this.step - 1);
      this.focusHeading();
    }

    showStep(step) {
      this.step = step;
      this.querySelectorAll('[data-step]').forEach((panel) => {
        panel.hidden = Number(panel.dataset.step) !== step;
      });
      this.querySelector('[data-back]').hidden = step === 1;
      this.querySelector('[data-progress-copy]').textContent = `Question ${step} of 4`;
      this.querySelectorAll('[data-progress-step]').forEach((item) => {
        item.classList.toggle('is-complete', Number(item.dataset.progressStep) < step);
        if (Number(item.dataset.progressStep) === step) item.setAttribute('aria-current', 'step');
        else item.removeAttribute('aria-current');
      });
      this.error.textContent = '';
    }

    focusHeading() {
      this.querySelector(`[data-step="${this.step}"] h2`).focus({ preventScroll: true });
      this.scrollToQuiz();
    }

    scrollToQuiz() {
      const target = !this.quiz.hidden
        ? this.querySelector('.ring-stack-quiz__progress')
        : !this.results.hidden ? this.results : this.hero;
      window.scrollTo({
        top: target.getBoundingClientRect().top + window.scrollY,
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
      });
    }

    availableVariants(product) {
      return product.variants.filter((variant) => variant.available && Object.entries(product.variantFilters).every(([name, value]) => {
        const index = product.optionNames.indexOf(name);
        return index >= 0 && variant.options[index] === value;
      }));
    }

    metalOptionIndex(product) {
      return product.optionNames.findIndex((name) => /metal|material|finish/i.test(name));
    }

    sizeOptionIndex(product) {
      return product.optionNames.findIndex((name) => /size/i.test(name));
    }

    variantMetal(product, variant) {
      const index = this.metalOptionIndex(product);
      if (index >= 0) return String(variant.options[index] || '');
      // The CSV supplies no metal attributes. Never guess an absent live option.
      return '';
    }

    preferredVariants(product) {
      return this.availableVariants(product).filter((variant) => {
        const metal = this.variantMetal(product, variant).toLowerCase();
        if (!metal) return false;
        if (this.answers.metal === 'mix') return true;
        const metals = ['gold', 'silver'].filter((value) => new RegExp(`\\b${value}\\b`).test(metal));
        return metals.length === 1 && metals[0] === this.answers.metal;
      });
    }

    sizeVariants(item) {
      const suggested = item.product.variants.find((variant) => variant.id === item.priceVariantId);
      if (!suggested) return [];
      const sizeIndex = this.sizeOptionIndex(item.product);
      // Lock every non-size option to the original quote, including the finish
      // assigned to this ring for Mix them. Never infer a metal from its size.
      return this.preferredVariants(item.product).filter((variant) => variant.options.every((option, index) =>
        index === sizeIndex || option === suggested.options[index]
      ));
    }

    sizeLabel(product, variant) {
      const index = this.sizeOptionIndex(product);
      return index >= 0 ? variant.options[index] : 'One size';
    }

    matchingProducts() {
      return this.products.filter((product) => product.categories.includes(this.answers.frequency)
        && product.vibes.includes(this.answers.vibe) && this.preferredVariants(product).length);
    }

    compareStacks(a, b) {
      if (a.total !== b.total) return a.total - b.total;
      // Equal-price eligible stacks follow the merchant's CSV order, not legacy scores.
      for (let index = 0; index < a.items.length; index += 1) {
        const difference = a.items[index].product.order - b.items[index].product.order;
        if (difference) return difference;
      }
      return 0;
    }

    pairingRule() {
      return this.pairingRules?.find((rule) => rule.vibe === this.answers.vibe) || null;
    }

    pairingState(items, rule = this.pairingRule()) {
      if (!rule) return { triggered: false, required: false };
      return {
        triggered: items.some((item) => rule.triggerIds.has(item.product.id)),
        required: items.some((item) => rule.requiredIds.has(item.product.id))
      };
    }

    stackMeetsPairingRule(items, rule = this.pairingRule()) {
      const state = this.pairingState(items, rule);
      return !state.triggered || state.required;
    }

    variantMetalType(product, variant) {
      const metal = this.variantMetal(product, variant).toLowerCase();
      if (/\bgold\b/.test(metal)) return 'gold';
      if (/\bsilver\b/.test(metal)) return 'silver';
      return '';
    }

    metalState(items) {
      const metals = new Set(items.map((item) => this.variantMetalType(item.product, this.displayVariant(item))));
      return { gold: metals.has('gold'), silver: metals.has('silver') };
    }

    stackMeetsMetalRule(items) {
      if (this.answers.metal !== 'mix') return true;
      const state = this.metalState(items);
      return state.gold && state.silver;
    }

    stackMeetsRecommendationRules(items, pairingRule = this.pairingRule()) {
      return this.stackMeetsPairingRule(items, pairingRule) && this.stackMeetsMetalRule(items);
    }

    replacementRecommendation(product, items, itemIndex) {
      let best = null;
      for (const variant of this.preferredVariants(product)) {
        const recommendation = { product, priceVariantId: variant.id, variantId: '' };
        const proposed = items.map((item, index) => index === itemIndex ? recommendation : item);
        if (this.stackMeetsRecommendationRules(proposed)
          && (!best || variant.price < this.displayVariant(best).price)) best = recommendation;
      }
      return best;
    }

    recommendationStateKey(total, items, maximum, rule) {
      const pairing = this.pairingState(items, rule);
      const metals = this.metalState(items);
      return `${maximum === Infinity ? 0 : total}:${Number(pairing.triggered)}${Number(pairing.required)}:${Number(metals.gold)}${Number(metals.silver)}`;
    }

    variantPriceOptions(product) {
      const prices = new Map();
      this.preferredVariants(product).forEach((variant) => {
        const key = this.answers.metal === 'mix'
          ? `${variant.price}:${this.variantMetalType(product, variant)}`
          : variant.price;
        if (!prices.has(key)) prices.set(key, variant);
      });
      return prices.values();
    }

    buildCuratedStack(productIds) {
      const products = productIds.map((id) => this.products.find((product) => product.id === id));
      if (products.some((product) => !product || !product.categories.includes(this.answers.frequency))
        || !products.some((product) => product.vibes.includes(this.answers.vibe))) return null;
      const maximum = { under100: 10000, from100: 20000, from200: Infinity }[this.answers.budget];
      const rule = this.pairingRule();
      let states = new Map([['0:00:00', { total: 0, items: [] }]]);
      for (const product of products) {
        const next = new Map();
        for (const state of states.values()) for (const variant of this.variantPriceOptions(product)) {
          const total = state.total + variant.price;
          if (total > maximum) continue;
          const candidate = { total, items: [...state.items, { product, priceVariantId: variant.id, variantId: '' }] };
          const key = this.recommendationStateKey(total, candidate.items, maximum, rule);
          const previous = next.get(key);
          if (!previous || this.compareStacks(candidate, previous) < 0) next.set(key, candidate);
        }
        states = next;
      }
      let best = null;
      for (const state of states.values()) {
        if (!this.stackMeetsRecommendationRules(state.items, rule)) continue;
        if (!best || this.compareStacks(state, best) < 0) best = state;
      }
      return best ? { count: productIds.length, items: best.items, curated: true } : null;
    }

    *generatedStackCandidates(count, excludedIds) {
      const products = this.matchingProducts().filter((product) => !excludedIds.has(product.id));
      const combinations = function* (start, selected) {
        if (selected.length === count) {
          yield selected;
          return;
        }
        for (let index = start; index <= products.length - (count - selected.length); index += 1) {
          yield* combinations(index + 1, [...selected, products[index]]);
        }
      };
      for (const selected of combinations(0, [])) {
        const stack = this.buildCuratedStack(selected.map((product) => product.id));
        if (stack) yield { ...stack, curated: false };
      }
    }

    solveUniqueFallbacks(counts, excludedIds) {
      const selected = new Map();
      const search = (position, usedIds) => {
        if (position === counts.length) return true;
        const count = counts[position];
        for (const stack of this.generatedStackCandidates(count, new Set([...excludedIds, ...usedIds]))) {
          selected.set(count, stack);
          const nextUsed = new Set([...usedIds, ...stack.items.map((item) => item.product.id)]);
          if (search(position + 1, nextUsed)) return true;
        }
        selected.delete(count);
        return false;
      };
      return search(0, new Set()) ? selected : null;
    }

    buildStacks() {
      const caps = new Map([[2, 1], [3, 2], [4, 1]]);
      const qualifying = this.curatedStackDefinitions.map((definition) => this.buildCuratedStack(definition)).filter(Boolean);
      const curated = new Map([2, 3, 4].map((count) => [
        count,
        qualifying.filter((stack) => stack.count === count).slice(0, caps.get(count))
      ]));
      const displayedCuratedIds = new Set(Array.from(curated.values()).flatMap((stacks) =>
        stacks.flatMap((stack) => stack.items.map((item) => item.product.id))
      ));
      const missing = [2, 3, 4].filter((count) => !curated.get(count).length);
      const possible = missing.filter((count) => !this.generatedStackCandidates(count, displayedCuratedIds).next().done);
      let fallbacks = this.solveUniqueFallbacks([...possible].sort((a, b) => b - a), displayedCuratedIds);
      if (!fallbacks) {
        fallbacks = new Map();
        possible.forEach((count) => {
          const stack = this.generatedStackCandidates(count, displayedCuratedIds).next().value;
          if (stack) fallbacks.set(count, stack);
        });
      }
      return [2, 3, 4].flatMap((count) => curated.get(count).length
        ? curated.get(count)
        : [fallbacks?.get(count) || { count, items: null }]);
    }

    buildGeneratedStacks() {
      const maximum = { under100: 10000, from100: 20000, from200: Infinity }[this.answers.budget];
      const rule = this.pairingRule();
      const states = Array.from({ length: 5 }, () => new Map());
      states[0].set('0:00', { total: 0, items: [] });

      // Multiple-choice knapsack in integer cents. Descending counts prevent duplicate
      // products. Keep one available variant per distinct price, not every ring size.
      for (const product of this.matchingProducts()) {
        for (let count = 4; count >= 1; count -= 1) {
          for (const state of states[count - 1].values()) {
            for (const variant of this.variantPriceOptions(product)) {
              const total = state.total + variant.price;
              if (total > maximum) continue;
              // Pairing states must remain distinct at equal prices so a required
              // companion candidate cannot be overwritten before the stack is complete.
              const candidate = { total, items: [...state.items, { product, priceVariantId: variant.id, variantId: '' }] };
              const key = this.recommendationStateKey(total, candidate.items, maximum, rule);
              const previous = states[count].get(key);
              if (!previous || this.compareStacks(candidate, previous) < 0) states[count].set(key, candidate);
            }
          }
        }
      }
      return [2, 3, 4].map((count) => {
        let best = null;
        for (const state of states[count].values()) {
          if (!this.stackMeetsRecommendationRules(state.items, rule)) continue;
          if (!best || this.compareStacks(state, best) < 0) best = state;
        }
        return { count, items: best ? best.items.map((item) => ({ ...item })) : null };
      });
    }

    showResults() {
      this.resultsRequested = !this.catalogueReady;
      this.quiz.hidden = true;
      this.results.hidden = false;
      this.cartStatus.textContent = '';
      if (this.catalogueReady) {
        this.querySelector('[data-catalogue-status]').hidden = true;
        this.querySelector('[data-results-content]').hidden = false;
        this.stacks = this.buildStacks();
        this.renderResults();
      } else {
        this.stacks = [];
        this.updateCatalogueStatus();
      }
      this.results.querySelector('h2').focus({ preventScroll: true });
      this.scrollToQuiz();
    }

    money(cents) {
      return this.moneyFormatter.format(cents / 100);
    }

    displayVariant(item) {
      return item.product.variants.find((variant) => variant.id === (item.variantId || item.priceVariantId));
    }

    selectedVariant(item) {
      return this.sizeVariants(item).find((variant) => variant.id === item.variantId);
    }

    promotionState(stack) {
      const eligibleIndex = stack.items.findIndex((item) => this.promotion.eligibleIds.has(item.product.id));
      const nonNewSubtotal = stack.items.reduce((total, item) => this.promotion.eligibleIds.has(item.product.id)
        ? total : total + this.displayVariant(item).price, 0);
      return {
        eligibleIndex,
        qualifies: eligibleIndex >= 0 && nonNewSubtotal >= this.promotion.thresholdCents
      };
    }

    bulkAddItems(stack) {
      const promotion = this.promotionState(stack);
      return promotion.qualifies
        ? stack.items.filter((item, index) => index !== promotion.eligibleIndex)
        : stack.items;
    }

    renderResults(focusSelector = '') {
      const container = this.querySelector('[data-stack-options]');
      container.replaceChildren();
      const hasMatches = this.stacks.some((stack) => stack.items !== null);
      this.querySelector('[data-no-matches]').hidden = hasMatches;
      this.querySelector('[data-matched-results]').hidden = !hasMatches;
      this.stacks.forEach((stack, stackIndex) => {
        // Keep original indexes for selection, swap, remove, and cart operations.
        if (!stack.items) return;
        const panel = document.createElement('section');
        panel.className = 'ring-stack-quiz__stack';
        panel.dataset.stack = stackIndex;
        panel.dataset.curated = String(Boolean(stack.curated));
        const header = document.createElement('header');
        header.className = 'ring-stack-quiz__stack-header';
        const title = document.createElement('h3');
        title.id = `${this.id}-stack-${stackIndex}`;
        title.textContent = `${stack.count}-ring stack`;
        const total = document.createElement('p');
        total.className = 'ring-stack-quiz__stack-total';
        total.dataset.stackTotal = stackIndex;
        total.setAttribute('aria-live', 'polite');
        const totalGroup = document.createElement('div');
        totalGroup.className = 'ring-stack-quiz__stack-total-group';
        const discountLabel = document.createElement('span');
        discountLabel.className = 'ring-stack-quiz__discount-label';
        discountLabel.textContent = 'discounts applied in cart';
        totalGroup.append(total, discountLabel);
        header.append(title, totalGroup);
        panel.setAttribute('aria-labelledby', title.id);
        panel.append(header);
        const genericPromotion = document.createElement('p');
        genericPromotion.className = 'ring-stack-quiz__promotion-message';
        genericPromotion.dataset.stackPromotion = '';
        genericPromotion.textContent = 'STACK WEEK: Choose a free stacking ring in the cart when you spend $98';
        panel.append(genericPromotion);
        const grid = document.createElement('div');
        grid.className = 'ring-stack-quiz__result-grid';
        stack.items.forEach((item, itemIndex) => grid.append(this.createProductCard(item, stackIndex, itemIndex)));
        const footer = document.createElement('footer');
        footer.className = 'ring-stack-quiz__stack-footer';
        const addButton = document.createElement('button');
        addButton.type = 'button';
        addButton.className = 'button button--primary';
        addButton.dataset.stackAdd = stackIndex;
        addButton.textContent = this.defaultAddAllText;
        addButton.setAttribute('aria-label', `Add all products from the ${stack.count}-ring stack to bag`);
        addButton.setAttribute('aria-live', 'polite');
        addButton.setAttribute('aria-atomic', 'true');
        addButton.addEventListener('click', () => this.addStackToCart(stackIndex, addButton));
        const sizePrompt = document.createElement('p');
        sizePrompt.className = 'ring-stack-quiz__size-prompt';
        sizePrompt.id = `${this.id}-stack-size-prompt-${stackIndex}`;
        sizePrompt.dataset.stackSizePrompt = stackIndex;
        sizePrompt.textContent = 'Choose your ring sizes first';
        addButton.setAttribute('aria-describedby', sizePrompt.id);
        footer.append(sizePrompt, addButton);
        panel.append(grid, footer);
        if (!stack.items.length) {
          const empty = document.createElement('p');
          empty.textContent = 'You removed all rings from this stack. Choose another stack or restart the quiz.';
          panel.append(empty);
        }
        container.append(panel);
      });
      this.updateTotals();
      this.syncCartAction();
      if (focusSelector) this.querySelector(focusSelector)?.focus();
    }

    updateTotals() {
      this.stacks.forEach((stack, index) => {
        if (!stack.items) return;
        const total = this.bulkAddItems(stack).reduce((sum, item) => sum + this.displayVariant(item).price, 0);
        this.querySelector(`[data-stack-total="${index}"]`).textContent = `Total ${this.money(total)}`;
      });
      this.updatePromotions();
    }

    updatePromotions() {
      this.stacks.forEach((stack, stackIndex) => {
        if (!stack.items) return;
        const panel = this.querySelector(`[data-stack="${stackIndex}"]`);
        if (!panel) return;
        const promotion = this.promotionState(stack);
        panel.querySelector('[data-stack-promotion]').hidden = false;
        panel.querySelectorAll('[data-product-promotion]').forEach((label, index) => {
          const isPromotedRing = index === promotion.eligibleIndex;
          const isFreeInCart = isPromotedRing && promotion.qualifies;
          label.hidden = !isPromotedRing;
          label.classList.toggle('is-free', isFreeInCart);
          if (!label.hidden) label.textContent = isFreeInCart
            ? 'Stack Week: Free in Cart'
            : 'Free for Stack Week on carts $98+';
          const card = label.closest('.ring-stack-quiz__card');
          const variantField = card?.querySelector('.ring-stack-quiz__variant-field');
          const cartSizeInstruction = card?.querySelector('[data-cart-size-instruction]');
          if (variantField) {
            variantField.hidden = isFreeInCart;
            variantField.querySelector('select').required = !isFreeInCart;
          }
          if (cartSizeInstruction) cartSizeInstruction.hidden = !isFreeInCart;
        });
      });
    }

    appendFixedOptions(body, product, variant) {
      const metal = document.createElement('p');
      metal.className = 'ring-stack-quiz__card-metal';
      metal.textContent = this.variantMetal(product, variant);
      body.append(metal);
      product.optionNames.forEach((name, index) => {
        if (index === this.metalOptionIndex(product) || index === this.sizeOptionIndex(product)) return;
        const detail = document.createElement('p');
        detail.className = 'ring-stack-quiz__card-metal';
        detail.textContent = `${name}: ${variant.options[index]}`;
        body.append(detail);
      });
    }

    createProductCard(item, stackIndex = -1, itemIndex = -1, choose = null) {
      const { product } = item;
      const suggested = product.variants.find((variant) => variant.id === item.priceVariantId);
      const card = document.createElement('article');
      card.className = 'ring-stack-quiz__card';
      const media = document.createElement('div');
      media.className = 'ring-stack-quiz__card-media';
      const imageUrl = suggested?.image || product.image;
      if (imageUrl) {
        const image = document.createElement('img');
        image.src = imageUrl;
        image.alt = '';
        image.loading = 'lazy';
        image.width = 900;
        image.height = 1125;
        image.dataset.cardImage = '';
        media.append(image);
      }
      const body = document.createElement('div');
      body.className = 'ring-stack-quiz__card-body';
      const title = document.createElement('h4');
      title.textContent = product.title;
      body.append(title);
      if (!choose) {
        const promotion = document.createElement('p');
        promotion.className = 'ring-stack-quiz__promotion-label';
        promotion.dataset.productPromotion = '';
        promotion.hidden = true;
        media.append(promotion);
      }
      this.appendFixedOptions(body, product, suggested);
      if (choose) {
        const price = document.createElement('p');
        price.textContent = this.money(suggested.price);
        body.append(price);
      } else {
        const variantSelector = this.createVariantSelector(item, stackIndex, itemIndex);
        const cartSizeInstruction = document.createElement('p');
        cartSizeInstruction.className = 'ring-stack-quiz__cart-size-instruction';
        cartSizeInstruction.dataset.cartSizeInstruction = '';
        cartSizeInstruction.textContent = 'Select your Size in Cart';
        cartSizeInstruction.hidden = true;
        body.append(variantSelector, cartSizeInstruction);
      }
      const actions = document.createElement('div');
      actions.className = 'ring-stack-quiz__card-actions';
      if (choose) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'button button--secondary ring-stack-quiz__product-action';
        button.textContent = 'choose this ring';
        button.addEventListener('click', choose);
        actions.append(button);
      } else {
        const swap = document.createElement('button');
        swap.type = 'button';
        swap.className = 'button button--secondary ring-stack-quiz__product-action';
        swap.textContent = 'swap';
        swap.setAttribute('aria-label', `Swap ${product.title}`);
        swap.disabled = this.submitting;
        swap.addEventListener('click', () => this.openModal(stackIndex, itemIndex, swap));
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'ring-stack-quiz__remove-link';
        remove.textContent = 'remove';
        remove.setAttribute('aria-label', `Remove ${product.title}`);
        remove.disabled = this.submitting;
        remove.addEventListener('click', () => {
          if (this.submitting) return;
          const remaining = this.stacks[stackIndex].items.filter((item, index) => index !== itemIndex);
          if (!this.stackMeetsPairingRule(remaining)) {
            this.cartStatus.textContent = 'Keep at least one companion ring with the stone rings in this stack.';
            return;
          }
          if (!this.stackMeetsMetalRule(remaining)) {
            this.cartStatus.textContent = 'Keep at least one gold and one sterling silver piece in a mixed metal stack.';
            return;
          }
          this.stacks[stackIndex].items.splice(itemIndex, 1);
          this.renderResults(`[data-stack="${stackIndex}"] h3`);
          this.cartStatus.textContent = `${product.title} removed from the ${this.stacks[stackIndex].count}-ring stack.`;
        });
        actions.append(swap, remove);
      }
      body.append(actions);
      card.append(media, body);
      return card;
    }

    createVariantSelector(item, stackIndex, itemIndex) {
      const field = document.createElement('label');
      field.className = 'ring-stack-quiz__variant-field';
      const select = document.createElement('select');
      select.dataset.variant = `${stackIndex}-${itemIndex}`;
      select.setAttribute('aria-label', `Size for ${item.product.title} in the ${this.stacks[stackIndex].count}-ring stack`);
      select.required = true;
      const variants = this.sizeVariants(item);
      select.disabled = !variants.length || this.submitting;
      const prompt = document.createElement('option');
      prompt.value = '';
      prompt.textContent = variants.length ? 'Size' : 'No available sizes';
      select.append(prompt);
      variants.forEach((variant) => {
        const option = document.createElement('option');
        option.value = variant.id;
        option.textContent = this.sizeLabel(item.product, variant);
        select.append(option);
      });
      select.value = item.variantId;
      select.addEventListener('change', () => {
        if (this.submitting) return;
        item.variantId = select.value;
        const image = field.closest('.ring-stack-quiz__card')?.querySelector('[data-card-image]');
        const selected = this.selectedVariant(item);
        if (image && selected) image.src = selected.image || item.product.image;
        this.cartStatus.textContent = '';
        this.updateTotals();
        this.syncCartAction();
      });
      const wrapper = document.createElement('span');
      wrapper.className = 'ring-stack-quiz__size-select';
      wrapper.append(select);
      field.append(wrapper);
      return field;
    }

    syncCartAction() {
      this.querySelectorAll('[data-stack-add]').forEach((button) => {
        const stack = this.stacks[Number(button.dataset.stackAdd)];
        const items = stack?.items ? this.bulkAddItems(stack) : [];
        const ready = Boolean(items.length && items.every((item) => this.selectedVariant(item)));
        const prompt = this.querySelector(`[data-stack-size-prompt="${button.dataset.stackAdd}"]`);
        if (prompt) prompt.hidden = ready || !items.length;
        button.disabled = !ready || this.submitting || this.cartFeedbackActive;
        button.setAttribute('aria-disabled', String(button.disabled));
        if (stack?.items) {
          const excludesFreeRing = items.length !== stack.items.length;
          button.setAttribute('aria-label', excludesFreeRing
            ? `Add ${items.length} paid products from the ${stack.count}-ring stack to bag; add the Stack Week free ring in the cart`
            : `Add all products from the ${stack.count}-ring stack to bag`);
        }
      });
      this.querySelectorAll('[data-stack] h3').forEach((heading) => { heading.tabIndex = -1; });
    }

    setCartInteractionsDisabled(disabled) {
      this.querySelectorAll('[data-stack] select, [data-stack] .ring-stack-quiz__product-action, [data-stack] .ring-stack-quiz__remove-link')
        .forEach((control) => { control.disabled = disabled; });
      this.querySelector('[data-restart]').disabled = disabled;
    }

    async addStackToCart(stackIndex, button) {
      if (this.submitting || this.cartFeedbackActive) return;
      const stack = this.stacks[stackIndex];
      const bulkAddItems = stack?.items ? this.bulkAddItems(stack) : [];
      if (!bulkAddItems.length || !bulkAddItems.every((item) => this.selectedVariant(item))) {
        this.cartStatus.textContent = 'Choose an available size for every ring being added before adding the stack.';
        this.syncCartAction();
        return;
      }
      const quantities = new Map();
      bulkAddItems.forEach((item) => {
        const id = this.selectedVariant(item).id;
        quantities.set(id, (quantities.get(id) || 0) + 1);
      });
      const items = Array.from(quantities, ([id, quantity]) => ({ id, quantity }));
      const pieceCount = bulkAddItems.length;
      this.submitting = true;
      this.activeCartButton = button;
      this.setCartInteractionsDisabled(true);
      this.syncCartAction();
      button.setAttribute('aria-busy', 'true');
      button.textContent = 'Adding your stack to the bag…';
      this.cartStatus.textContent = '';
      this.cartNotification?.setActiveElement?.(button);
      const body = { items };
      if (this.cartNotification?.getSectionsToRender) {
        body.sections = this.cartNotification.getSectionsToRender().map((section) => section.id);
        body.sections_url = window.location.pathname;
      }
      let added = false;
      try {
        const cartAddUrl = window.routes?.cart_add_url || `${window.Shopify?.routes?.root || '/'}cart/add`;
        const response = await fetch(`${cartAddUrl}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body)
        });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(result.description || 'The selected rings could not be added.');
        added = true;
        this.cartFeedbackActive = true;
        button.textContent = `${pieceCount} pieces added to your bag`;
        this.cartStatus.textContent = '';
        if (this.cartNotification?.renderContents && result.sections) this.cartNotification.renderContents(result);
      } catch (error) {
        console.error('Ring stack quiz cart action:', error);
        if (!added) {
          button.textContent = this.defaultAddAllText;
          this.cartStatus.textContent = `${error.message || 'We could not confirm the cart update.'} Check your bag before trying again.`;
        }
      } finally {
        this.submitting = false;
        button.removeAttribute('aria-busy');
        this.setCartInteractionsDisabled(false);
        this.syncCartAction();
        if (added) {
          clearTimeout(this.cartFeedbackTimer);
          this.cartFeedbackTimer = setTimeout(() => {
            this.cartFeedbackActive = false;
            if (button.isConnected) button.textContent = this.defaultAddAllText;
            this.activeCartButton = null;
            this.syncCartAction();
          }, 3000);
        }
      }
    }

    openModal(stackIndex, itemIndex, trigger) {
      if (this.submitting) return;
      this.swapTarget = { stackIndex, itemIndex };
      this.returnFocus = trigger;
      const items = this.stacks[stackIndex].items;
      const handles = items.map((item) => item.product.handle);
      const alternatives = this.matchingProducts()
        .filter((product) => !handles.includes(product.handle))
        .map((product) => this.replacementRecommendation(product, items, itemIndex))
        .filter(Boolean);
      const grid = this.querySelector('[data-swap-grid]');
      grid.replaceChildren();
      alternatives.forEach((recommendation) => {
        grid.append(this.createProductCard(recommendation, -1, -1, () => this.chooseSwap(recommendation)));
      });
      if (!alternatives.length) {
        const message = document.createElement('p');
        message.textContent = 'No other matching rings are currently available.';
        grid.append(message);
      }
      this.modal.hidden = false;
      document.body.classList.add('ring-stack-quiz-modal-open');
      document.addEventListener('keydown', this.handleModalKeydown);
      const close = this.modal.querySelector('.ring-stack-quiz__modal-close');
      if (typeof window.trapFocus === 'function') window.trapFocus(this.modal, close);
      else close.focus();
    }

    chooseSwap(recommendation) {
      if (this.submitting || !this.swapTarget) return;
      const { stackIndex, itemIndex } = this.swapTarget;
      this.stacks[stackIndex].items.splice(itemIndex, 1, recommendation);
      this.closeModal();
      this.renderResults(`[data-variant="${stackIndex}-${itemIndex}"]`);
      this.cartStatus.textContent = '';
    }

    closeModal() {
      if (!this.modal || this.modal.hidden) return;
      this.modal.hidden = true;
      document.body.classList.remove('ring-stack-quiz-modal-open');
      document.removeEventListener('keydown', this.handleModalKeydown);
      if (typeof window.removeTrapFocus === 'function') window.removeTrapFocus();
      if (this.returnFocus?.isConnected) this.returnFocus.focus();
      this.returnFocus = null;
      this.swapTarget = null;
    }

    onModalKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeModal();
      }
      if (event.key !== 'Tab' || typeof window.trapFocus === 'function') return;
      const focusable = Array.from(this.modal.querySelectorAll('button:not([disabled]):not([tabindex="-1"]), a[href]'));
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    restart() {
      if (this.submitting) return;
      clearTimeout(this.cartFeedbackTimer);
      this.cartFeedbackActive = false;
      if (this.activeCartButton?.isConnected) this.activeCartButton.textContent = this.defaultAddAllText;
      this.activeCartButton = null;
      this.resultsRequested = false;
      this.closeModal();
      this.answers = { frequency: '', vibe: '', metal: '', budget: '' };
      this.stacks = [];
      this.cartStatus.textContent = '';
      this.querySelectorAll('[data-choice]').forEach((button) => button.setAttribute('aria-pressed', 'false'));
      this.results.hidden = true;
      this.quiz.hidden = true;
      this.hero.hidden = false;
      this.showStep(1);
      this.querySelector('[data-start]').focus({ preventScroll: true });
      this.scrollToQuiz();
    }
  }

  customElements.define('ring-stack-quiz', RingStackQuiz);
})();
